import { JSX, useContext, useEffect, useRef, useState } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import { FlyoutLink } from "@app/shared/Flyouts";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { BulkProjectTypeEnum, BulkTargetingWizardSteps, BulkTargetingApiResponseModel, BulkCreateProviderTargetingModel, BulkTargetIncludedReports } from "@app/targeting/import/interfaces";
import TargetingWizardConfigureReport from "@app/targeting/import/TargetingWizardConfigureReport";
import { BulkTargetingWizardProvider, BulkTargetingWizardContext } from "@app/targeting/import/TargetingWizardContext";
import TargetingWizardFlyout from "@app/targeting/import/TargetingWizardFlyout";
import TargetingWizardImportList from "@app/targeting/import/TargetingWizardImportList";
import TargetingWizardSelectProjects from "@app/targeting/import/TargetingWizardSelectProjects";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import TargetingWizardColumnConfiguration from "@app/targeting/import/TargetingWizardColumnConfiguration";

const BulkTargetingWizard = (): JSX.Element => {
  const location = useLocation();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const isZipVolumeTargetingEnabled = featureFlags.has(Features.ZIP_CODE_ANALYSIS_VOLUME_TARGETING);
  const requestedProjectType = new URLSearchParams(location.search).get('projectType') === BulkProjectTypeEnum.Zip
    ? BulkProjectTypeEnum.Zip
    : BulkProjectTypeEnum.County;
  const projectType = requestedProjectType === BulkProjectTypeEnum.Zip && !isZipVolumeTargetingEnabled
    ? BulkProjectTypeEnum.County
    : requestedProjectType;
  return (
    <BulkTargetingWizardProvider projectType={projectType}>
      <BulkTargetingWizardContent />
    </BulkTargetingWizardProvider>
  );
};

const BulkTargetingWizardContent = (): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const isVolumeTargetingReportConfigurationEnabled = featureFlags.has(Features.VOLUME_TARGETING_REPORT_CONFIGURATION);
  const isZipVolumeTargetingEnabled = featureFlags.has(Features.ZIP_CODE_ANALYSIS_VOLUME_TARGETING);
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const [pageIsValid, setPageIsValid] = useState<boolean>(false);
  const [conflicts, setConflicts] = useState<BulkTargetingApiResponseModel[]>([]);
  const [redirectToView, setRedirectToView] = useState(false);
  const [preConflictNumberOfSelections, setPreConflictNumberOfSelections] = useState(0);

  // Conditional page names based on feature flag
  const humanReadablePageNames = isVolumeTargetingReportConfigurationEnabled 
    ? {
        [BulkTargetingWizardSteps.SelectProjects]: "Select Projects",
        [BulkTargetingWizardSteps.ImportList]: "Import List",
        [BulkTargetingWizardSteps.ConfigureReport]: "Configure Report",
        [BulkTargetingWizardSteps.ColumnConfiguration]: "Column Configuration",
      }
    : {
        [BulkTargetingWizardSteps.SelectProjects]: "Select Projects",
        [BulkTargetingWizardSteps.ImportList]: "Import List",
        [BulkTargetingWizardSteps.ConfigureReport]: "Configure Report",
      };

  useEffect(() => {
    focusFirstInteractiveElement(wizardBodyRef);
  }, [wizardContext.page]);

  const attemptSubmit = async () => {
    const {
      bulkProviderTargetId,
      comment,
      emailOnSuccess,
      filename,
      formatType,
      eligibleSelectedProjectIds,
      targetingReason,
      existingTargetAction,
      isDistinctServicingProviderReport,
      selectedReports,
      columnConfigurations,
      includeReportMetadataSummary
    } = wizardContext;

    if (
      !bulkProviderTargetId ||
      eligibleSelectedProjectIds.length === 0 ||
      !filename ||
      !targetingReason ||
      (targetingReason === "Other" && !comment)
    ) {
      return false;
    }

    const reports = selectedReports ?? [];
    const getPresetId = (sectionKey: string): number | null => {
      const presetId = columnConfigurations?.[sectionKey]?.presetId;
      return presetId ? parseInt(presetId, 10) : null;
    };
    const isServiceAreaProvidersSelected = reports.includes("serviceAreaProviders");
    const isOpportunityProvidersSelected = reports.includes("opportunityProviders");
    const includedReports: BulkTargetIncludedReports = {
      distinctOpportunityProviders: isOpportunityProvidersSelected && isDistinctServicingProviderReport,
      distinctOpportunityProvidersReportCustomizationId: isDistinctServicingProviderReport ? getPresetId("opportunityProviders") : null,
      distinctServiceAreaProviders: isServiceAreaProvidersSelected && isDistinctServicingProviderReport,
      distinctServiceAreaProvidersReportCustomizationId: isDistinctServicingProviderReport ? getPresetId("serviceAreaProviders") : null,
      includeReportMetadataSummary,
      opportunityChangedGaps: reports.includes("opportunityChangedGaps"),
      opportunityChangedGapsReportCustomizationId: getPresetId("opportunityChangedGaps"),
      opportunityProviders: isOpportunityProvidersSelected && !isDistinctServicingProviderReport,
      opportunityProvidersReportCustomizationId: !isDistinctServicingProviderReport ? getPresetId("opportunityProviders") : null,
      opportunitySpecialtySummaryComparison: reports.includes("opportunitySpecialtySummaryComparison"),
      opportunitySpecialtySummaryComparisonReportCustomizationId: getPresetId("opportunitySpecialtySummaryComparison"),
      serviceAreaChangedGaps: reports.includes("serviceAreaChangedGaps"),
      serviceAreaChangedGapsReportCustomizationId: getPresetId("serviceAreaChangedGaps"),
      serviceAreaProviders: isServiceAreaProvidersSelected && !isDistinctServicingProviderReport,
      serviceAreaProvidersReportCustomizationId: !isDistinctServicingProviderReport ? getPresetId("serviceAreaProviders") : null,
      serviceAreaSpecialtySummaryComparison: reports.includes("serviceAreaSpecialtySummaryComparison"),
      serviceAreaSpecialtySummaryComparisonReportCustomizationId: getPresetId("serviceAreaSpecialtySummaryComparison"),
      targetUploadFile: reports.includes("targetUploadFile"),
      volumeTargetProjectDetails: reports.includes("volumeTargetProjectDetails"),
      volumeTargetProjectDetailsReportCustomizationId: getPresetId("volumeTargetProjectDetails"),
    };

    const model: BulkCreateProviderTargetingModel = {
      bulkTargetId: bulkProviderTargetId,
      columnConfigurations,
      customProjectIds: eligibleSelectedProjectIds.map((id) => parseInt(id, 10)),
      emailOnSuccess,
      existingTargetAction,
      exportName: filename,
      formatType,
      includedReports,
      isDistinctServicingProviderReport,
      projectType: wizardContext.projectType,
      reasonDescription: comment,
      reasonName: targetingReason,
      selectedReports: selectedReports || []
    };

    await wizardContext.projectionProviderService.bulkCreateProviderTargeting(model);
    setRedirectToView(true);
    return true;
  };

  const getConfirmationSidePanelContent = () => {
    return <TargetingWizardFlyout onSubmit={attemptSubmit} targetAction={wizardContext.existingTargetAction} />;
  };

  const validateAndAdvance = () => {
    switch (wizardContext.page) {
      case BulkTargetingWizardSteps.SelectProjects:
        if (wizardContext.eligibleSelectedProjectIds.length > 0) {
          wizardContext.setReviewProjects(
            wizardContext.projects.filter((p) => wizardContext.eligibleSelectedProjectIds.includes(p.projectId.toString())));
          wizardContext.setPage(BulkTargetingWizardSteps.ImportList);
        }
        break;
      case BulkTargetingWizardSteps.ImportList:
        if (!!wizardContext.bulkImportFormData) {
          wizardContext.setPage(BulkTargetingWizardSteps.ConfigureReport);
        }
        break;
      case BulkTargetingWizardSteps.ConfigureReport:
        if (isVolumeTargetingReportConfigurationEnabled) {
          if (wizardContext.selectedReports && wizardContext.selectedReports.length > 0) {
            wizardContext.setPage(BulkTargetingWizardSteps.ColumnConfiguration);
          }
        } else {
          // With feature flag disabled, skip Column Configuration step
          attemptSubmit();
        }
        break;
    } // The other possible value, .ConfigureReport, has a different button at the bottom
  };

  const renderCurrentWizardStep = () => {
    switch (wizardContext.page) {
      case BulkTargetingWizardSteps.SelectProjects:
        return <TargetingWizardSelectProjects stepIsValid={setPageIsValid} />;
      case BulkTargetingWizardSteps.ImportList:
        return <TargetingWizardImportList stepIsValid={setPageIsValid} />;
      case BulkTargetingWizardSteps.ConfigureReport:
        return <TargetingWizardConfigureReport stepIsValid={setPageIsValid} />;
      case BulkTargetingWizardSteps.ColumnConfiguration:
        if (isVolumeTargetingReportConfigurationEnabled) {
          return <TargetingWizardColumnConfiguration stepIsValid={setPageIsValid} />;
        }
        return null;
    }
  };

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case BulkTargetingWizardSteps.ImportList:
        wizardContext.setPage(BulkTargetingWizardSteps.SelectProjects);
        break;
      case BulkTargetingWizardSteps.ConfigureReport:
        wizardContext.setPage(BulkTargetingWizardSteps.ImportList);
        break;
      case BulkTargetingWizardSteps.ColumnConfiguration:
        if (isVolumeTargetingReportConfigurationEnabled) {
          wizardContext.setPage(BulkTargetingWizardSteps.ConfigureReport);
        }
        break;
    }
  };

  const handleUpdatePage = (newPage: number) => {
    const step = Object.entries(BulkTargetingWizardSteps).find((entry) => parseInt(entry[0], 10) === newPage)[1] as BulkTargetingWizardSteps;
    wizardContext.setPage(BulkTargetingWizardSteps[step] as unknown as BulkTargetingWizardSteps);
    setConflicts([]);
    setPreConflictNumberOfSelections(wizardContext.totalSelectedProjectIds.length);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [wizardContext.page]);

  useEffect(() => {
    // if we have selected projects, hit api endpoint for fetching projects
    wizardContext.projectionProviderService.getBulkTargetingProjectsAsync(true, false, undefined, wizardContext.projectType ?? BulkProjectTypeEnum.County).then((response) => {
      // make sure no selected projects have changed into readonly mode
      const newConflicts: BulkTargetingApiResponseModel[] = [];
      const newSelectedProjectIds: string[] = [];
      response.forEach((r) => {
        if (wizardContext.totalSelectedProjectIds.includes(r.projectId.toString()) && r.isReadOnly) {
          newConflicts.push(r);
        } else if (wizardContext.totalSelectedProjectIds.includes(r.projectId.toString())) {
          newSelectedProjectIds.push(r.projectId.toString());
        }
      });
      // if they have, set the page back to 1 and trigger an alert banner to let the user know why they're back here, that their progress was saved, and what projects were effected
      if (newConflicts.length > 0 && newConflicts.length === wizardContext.totalSelectedProjectIds.length) {
        wizardContext.setPage(BulkTargetingWizardSteps.SelectProjects);
      }
      // pulling this into a variable because I don't know how React batches set calls, and don't want to use a state value that's changing in another set call
      const oldProjectsSelected = wizardContext.totalSelectedProjectIds.length;
      setPreConflictNumberOfSelections(oldProjectsSelected);
      if (newConflicts.length > 0) {
        wizardContext.setTotalSelectedProjectIds(newSelectedProjectIds);
        // filter the eligible project selections by the still-valid total selections
        wizardContext.setEligibleSelectedProjectIds((prevValue) => {
          const newValue = prevValue.filter((id) => newSelectedProjectIds.includes(id));
          return newValue;
        })
      }

      setConflicts(newConflicts);
    });
  }, [wizardContext.page]);

  if (redirectToView) {
    return <Redirect to="/targeting/view" push={false} />;
  }

  const isZip = wizardContext.projectType === BulkProjectTypeEnum.Zip;
  const wizardTitle = isZip ? "New Zip Code Targets" : "New Targets";

  return (
    <Page documentTitle={isZip ? "New Zip Code Volume Targeting" : "New Volume Targeting"}>
      {/* The design for the wizard does not have breadcrumbs */}
      <PageInner className="targeting-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">{wizardTitle}</h1>
        </header>
        <WizardSteps currentStep={wizardContext.page} steps={Object.values(humanReadablePageNames)} setStep={handleUpdatePage} />
        {conflicts.length > 0 && conflicts.length < preConflictNumberOfSelections && (
          <QESAlert classNames="mt-3 mb-3" type="info" collapseTitle="The following selected projects have been changed to read only mode and unselected:">
            <ul className="mb-0">
              {conflicts.map((conflict) => <li key={conflict.projectId}>{conflict.projectName}</li>)}
            </ul>
          </QESAlert>
        )}
        {conflicts.length > 0 && conflicts.length === preConflictNumberOfSelections && (
          <QESAlert classNames="mt-3 mb-3" type="info">
            All of the projects you selected have been changed to read only mode. No other selections made during the volume targeting setup process have been lost.
          </QESAlert>
        )}
        <hr />
        <div ref={wizardBodyRef}>
          {renderCurrentWizardStep()}
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/targeting/view" className="qes-button qes-button-secondary danger" data-testid="import-targeting__cancel">Cancel</Link>
        )}
        rightSide={(
          <div className="new-terms__nav-buttons">
            {wizardContext.page !== BulkTargetingWizardSteps.SelectProjects && <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="import-targeting__previous">Previous</QESButton>}
            {(isVolumeTargetingReportConfigurationEnabled && wizardContext.page === BulkTargetingWizardSteps.ColumnConfiguration) ? (
              <FlyoutLink
                disabled={!pageIsValid || (isZip && !isZipVolumeTargetingEnabled)}
                content={{
                  body: getConfirmationSidePanelContent(),
                  title: "Warning",
                }}
                classNames="qes-button-primary ms-3"
                testId="import-targeting-submit"
                size="40"
              >
                Submit
              </FlyoutLink>
            ) : wizardContext.page === BulkTargetingWizardSteps.ConfigureReport && !isVolumeTargetingReportConfigurationEnabled ? (
              <FlyoutLink
                disabled={!pageIsValid || (isZip && !isZipVolumeTargetingEnabled)}
                content={{
                  body: getConfirmationSidePanelContent(),
                  title: "Warning",
                }}
                classNames="qes-button-primary ms-3"
                testId="import-targeting-submit"
                size="40"
              >
                Submit
              </FlyoutLink>
            ) : (
              <QESButton onClick={validateAndAdvance} disabled={!pageIsValid} qesTestId="import-targeting__next" className="ms-3">Next</QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};

export default BulkTargetingWizard;